import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Sparkles, Loader2, RotateCcw, ChevronRight, User, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const QUESTIONS = [
  { id: "approach", q: "When facing a problem, you...", options: ["Charge in headfirst", "Analyze every angle first", "Rally others to help", "Find the unconventional path"] },
  { id: "motivation", q: "What drives you most?", options: ["Protecting those I love", "Seeking knowledge and truth", "Power and ambition", "Freedom and adventure"] },
  { id: "weakness", q: "Your biggest weakness is...", options: ["Being too trusting", "Overthinking everything", "Holding grudges", "Running from commitment"] },
  { id: "social", q: "At a gathering, you're the one who...", options: ["Leads the conversation", "Observes from the corner", "Makes everyone laugh", "Slips out early"] },
  { id: "moral", q: "A friend asks you to bend the rules. You...", options: ["Refuse — rules exist for a reason", "Depends on the reason", "Help without hesitation", "Bend them and add a few twists"] },
  { id: "power", q: "If you could have one ability...", options: ["Super strength", "Telepathy", "Time manipulation", "Shapeshifting"] },
  { id: "downtime", q: "Your ideal downtime is...", options: ["Training or working out", "Reading or researching", "Exploring somewhere new", "Creating something artistic"] },
];

const QuizPage = () => {
  const [universes, setUniverses] = useState([]);
  const [universeFilter, setUniverseFilter] = useState("");
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/universes`).then(r => setUniverses(r.data)).catch(() => {});
  }, []);

  const handleAnswer = (questionId, answer) => {
    const updated = { ...answers, [questionId]: answer };
    setAnswers(updated);
    if (currentQ < QUESTIONS.length - 1) {
      setTimeout(() => setCurrentQ(currentQ + 1), 300);
    }
  };

  const submitQuiz = async () => {
    if (Object.keys(answers).length < QUESTIONS.length) {
      toast.error("Please answer all questions");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quiz/matchmaker`, {
        answers,
        universe_filter: universeFilter || null,
      });
      setResult(res.data);
    } catch {
      toast.error("Quiz failed. Try again!");
    } finally { setLoading(false); }
  };

  const reset = () => {
    setAnswers({});
    setCurrentQ(0);
    setResult(null);
    setStarted(false);
  };

  const progress = (Object.keys(answers).length / QUESTIONS.length) * 100;

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-base)]">
      <SEOHead title="Character Matchmaker" description="Find out which fictional character matches your personality!" />
      <Navbar />
      <main className="flex-1 pt-20 pb-16 px-4 max-w-2xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-pink-500/20 bg-pink-500/5 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-pink-500" />
            <span className="text-xs font-mono tracking-wider text-pink-500 uppercase">Character Matchmaker</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-2">Which Character Are You?</h1>
          <p className="text-slate-400 text-sm">Answer {QUESTIONS.length} questions and AI will match you with your fictional alter ego</p>
        </div>

        {/* Result View */}
        {result ? (
          <div className="space-y-4 animate-fade-in" data-testid="quiz-result">
            <div className="card-surface p-6 sm:p-8 text-center">
              <p className="text-pink-400 text-xs font-mono uppercase tracking-wider mb-3">Your Match</p>
              {result.matched_character?.image && (
                <img src={result.matched_character.image} alt={result.match} className="w-28 h-28 rounded-full object-cover mx-auto border-3 border-pink-500/30 mb-4" />
              )}
              <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-1" data-testid="quiz-match-name">{result.match}</h2>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 mb-4">
                <Zap className="w-3.5 h-3.5 text-pink-500" />
                <span className="text-pink-400 font-mono text-sm font-bold">{result.match_percent}% Match</span>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed max-w-md mx-auto mb-4">{result.reason}</p>

              {result.traits_shared?.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center mb-6">
                  {result.traits_shared.map((t, i) => (
                    <span key={i} className="text-xs bg-amber-500/10 text-amber-400 px-3 py-1 rounded-full">{t}</span>
                  ))}
                </div>
              )}

              {result.runner_up && (
                <div className="pt-4 border-t border-white/5">
                  <p className="text-slate-500 text-[10px] font-mono uppercase tracking-wider mb-2">Runner Up</p>
                  <div className="flex items-center justify-center gap-3">
                    {result.runner_up_character?.image && (
                      <img src={result.runner_up_character.image} alt={result.runner_up} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                    )}
                    <div className="text-left">
                      <p className="text-white font-semibold text-sm">{result.runner_up}</p>
                      <p className="text-slate-500 text-xs">{result.runner_up_reason}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-center">
              <Button onClick={reset} className="gap-2 bg-white/5 hover:bg-white/10 text-white border border-white/10" data-testid="quiz-retry">
                <RotateCcw className="w-4 h-4" /> Try Again
              </Button>
              {result.matched_character?.id && (
                <Link to={`/character/${result.matched_character.id}`}>
                  <Button className="btn-primary gap-2">
                    View Character <ChevronRight className="w-4 h-4" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        ) : !started ? (
          /* Start Screen */
          <div className="card-surface p-8 text-center" data-testid="quiz-start">
            <Sparkles className="w-12 h-12 text-pink-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white font-['Outfit'] mb-2">Ready to discover your match?</h2>
            <p className="text-slate-400 text-sm mb-6">Optionally filter by a specific universe, or search across all.</p>
            <div className="max-w-xs mx-auto mb-6">
              <Select value={universeFilter} onValueChange={setUniverseFilter}>
                <SelectTrigger className="bg-[var(--bg-elevated)] border-white/10 text-white" data-testid="quiz-universe-filter">
                  <SelectValue placeholder="All Universes" />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-white/10">
                  <SelectItem value="all">All Universes</SelectItem>
                  {universes.map(u => <SelectItem key={u.id} value={u.slug}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => { setStarted(true); if (universeFilter === "all") setUniverseFilter(""); }} className="btn-primary px-8 py-3 text-base" data-testid="quiz-start-btn">
              Start Quiz
            </Button>
          </div>
        ) : (
          /* Questions */
          <div data-testid="quiz-questions">
            {/* Progress */}
            <div className="mb-6">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Question {currentQ + 1} of {QUESTIONS.length}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-white/5">
                <div className="h-full rounded-full bg-gradient-to-r from-pink-500 to-amber-500 transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>

            {/* Question Card */}
            <div className="card-surface p-6 sm:p-8 mb-4">
              <p className="text-white text-lg font-semibold font-['Outfit'] mb-5" data-testid={`quiz-q-${currentQ}`}>
                {QUESTIONS[currentQ].q}
              </p>
              <div className="space-y-2">
                {QUESTIONS[currentQ].options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => handleAnswer(QUESTIONS[currentQ].id, opt)}
                    className={`w-full text-left px-5 py-3.5 rounded-xl border transition-all text-sm ${
                      answers[QUESTIONS[currentQ].id] === opt
                        ? "bg-pink-500/15 border-pink-500/30 text-white"
                        : "bg-white/[0.03] border-white/5 text-slate-300 hover:bg-pink-500/5 hover:border-pink-500/15 hover:text-white"
                    }`}
                    data-testid={`quiz-opt-${currentQ}-${i}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Button
                variant="ghost"
                disabled={currentQ === 0}
                onClick={() => setCurrentQ(currentQ - 1)}
                className="text-slate-400"
                data-testid="quiz-prev"
              >
                Previous
              </Button>
              {currentQ < QUESTIONS.length - 1 ? (
                <Button
                  variant="ghost"
                  disabled={!answers[QUESTIONS[currentQ].id]}
                  onClick={() => setCurrentQ(currentQ + 1)}
                  className="text-white"
                  data-testid="quiz-next"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={submitQuiz}
                  disabled={Object.keys(answers).length < QUESTIONS.length || loading}
                  className="btn-primary gap-2"
                  data-testid="quiz-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {loading ? "Matching..." : "Find My Match"}
                </Button>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default QuizPage;
